import type { FootprintCountry } from "@modules/investor-relations/data";
import { GLOBE_INITIAL_VIEW } from "@modules/investor-relations/data";
import type { GeoPermissibleObjects, GeoProjection } from "d3-geo";
import { geoDistance, geoGraticule10, geoOrthographic, geoPath } from "d3-geo";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { feature } from "topojson-client";
import type { GeometryCollection, Topology } from "topojson-specification";
import type { EarthTexture } from "./earth-texture";
import { createEarthPainter, loadEarthTexture } from "./earth-texture";

/** Geometry of the globe stage, in viewBox units. */
export const GLOBE_WIDTH = 760;
export const GLOBE_HEIGHT = 620;
export const GLOBE_RADIUS = 250;
export const GLOBE_CENTER: [number, number] = [
  GLOBE_WIDTH / 2,
  GLOBE_HEIGHT / 2,
];

/**
 * Where the light falls on the sphere, in radii from its centre. Every shaded
 * layer keys off this one vector — the `fp-terminator` gradient in `GlobeVisual`
 * places its focus at the same spot (`cx="30%" cy="26%"` of the circle's bounding
 * box, which is exactly `centre + [-0.4r, -0.48r]`), so the canvas city lights
 * and the SVG shading fall away together instead of disagreeing.
 */
const LIGHT_OFFSET: [number, number] = [-0.4, -0.48];
/** Matches the terminator gradient's darkest stop, so the two stay in step. */
const LIGHT_FALLOFF = 0.55;
/**
 * The surface falls away from the light more gently than the lights on it do.
 * It has to: the `fp-terminator` gradient is painted in the SVG *over* the
 * texture canvas, so the night side is already being darkened once before this
 * is applied, and matching `LIGHT_FALLOFF` here would take it to flat black and
 * throw away the continents the texture was added for.
 */
const EARTH_LIGHT_FALLOFF = 0.4;

/** Natural Earth 110m country outlines, served as a static asset. */
const WORLD_URL = "/geo/countries-110m.json";
/**
 * Natural Earth 10m populated places, trimmed to `[lon, lat, tier]` triples.
 * Tier is a population bucket, 0 (20k+) to 5 (5m+), and drives how big and how
 * bright the light is — that weighting is what gives Europe, the Gulf and India
 * the density they have on a real night-side photograph.
 */
const LIGHTS_URL = "/geo/city-lights.json";

const ROTATION_DURATION = 1000;
/**
 * Half the rotation: how long the highlight takes to fade out before the shape
 * is swapped, and to fade back in afterwards. Must stay in step with the
 * `duration-500` transition on the highlight path in `GlobeVisual`.
 */
const HIGHLIGHT_FADE = ROTATION_DURATION / 2;
const PULSE_PERIOD = 1600;
/** Phase offset between markers, so the rings do not pulse in lockstep. */
const PULSE_STAGGER = 0.28;
const PULSE_MIN_RADIUS = 6;
const PULSE_GROWTH = 22;
/** Peak opacity of each expanding ring, outermost last. */
const RING_OPACITY = [0.75, 0.45, 0.28];
/** The selected marker's dot breathes around its resting radius. */
const CORE_RADIUS = 5.5;
const CORE_BREATH = 0.9;
const CORE_PERIOD = 700;

/** The selected country settles slightly above centre, clear of the trail. */
const SELECTION_TILT = 6;
/** How far the sphere zooms in on the country that is selected. */
const SELECTION_ZOOM = 3;
/** Beyond a quarter turn from the view centre a point is on the far side. */
const VISIBLE_ANGLE = Math.PI / 2;

/** Radius of the outward atmospheric bloom, as a multiple of the sphere's. */
const ATMOSPHERE_SPREAD = 1.5;

/** Light size in viewBox units at resting scale, indexed by population tier. */
const TIER_SIZE = [3.2, 4.2, 5.6, 7.5, 9.5, 12];
/** Light opacity, indexed by population tier. */
const TIER_ALPHA = [0.3, 0.38, 0.48, 0.6, 0.72, 0.85];
/**
 * How the sprite grows with the zoom. Scaling it *linearly* is the obvious
 * choice and the wrong one: at three times the scale every blob is three times
 * wider, so moving closer magnifies the smear instead of resolving detail, and
 * the overlap between neighbours turns into grain. Growing sub-linearly means
 * the cities spread apart faster than their halos do, so zooming in separates
 * them into points the way it should.
 */
const ZOOM_SIZE_EXPONENT = 0.45;
/**
 * How many dimmer satellites to scatter around a city of each tier. Real
 * conurbations bleed outwards; a bare point per city reads as a pinprick map
 * rather than the sprawl in the reference.
 */
const TIER_SATELLITES = [0, 1, 2, 4, 6, 9];
/** Satellites sit within this many degrees of their parent city. */
const SATELLITE_SPREAD = 0.45;
/**
 * Kept well under the parent city's. Satellites are scattered at random, so at
 * close zoom they are the one part of the field that reads as noise rather than
 * as geography — faint enough and they go back to being sprawl around a city.
 */
const SATELLITE_ALPHA = 0.36;
const SATELLITE_SIZE = 0.7;
/**
 * Below this tier a light is dropped when the field has to be thinned — on a
 * narrow stage, and on the frames of a rotation, where a full pass costs more
 * than the frame budget allows. See `drawLights`.
 */
const THIN_MIN_TIER = 3;
const THIN_BREAKPOINT = 640;

/** Each light is packed as [a, b, c, size, alpha] — see `packLights`. */
const LIGHT_STRIDE = 5;
/**
 * The sprite is stamped at a few viewBox units across at rest and several times
 * that at full zoom, so it is authored well above the size it is drawn at — the
 * solid core below only survives magnification if there are pixels behind it.
 */
const LIGHT_SPRITE_SIZE = 64;
/**
 * Where the sprite stops being a light and starts being the glow around it, as a
 * fraction of its radius. Everything inside this is painted flat and opaque, so
 * a city reads as a lit point with a halo rather than as a patch of fog.
 */
const LIGHT_CORE_STOP = 0.15;

/**
 * How far the lights canvas is stretched beyond the globe's own box, as a
 * fraction of it on each side.
 *
 * The SVG is `overflow-visible`, so when a selection zooms the projection to
 * three times its resting scale the sphere spills well outside the viewBox and
 * keeps drawing. A canvas cannot do that — it only ever paints inside its own
 * element — so at zoom the lights used to stop dead along a rectangle while the
 * land carried on past it. Oversizing the element gives them room to follow.
 * Wider than tall, because the section is wider than the stage but no taller.
 */
const OVERSCAN_X = 0.5;
const OVERSCAN_Y = 0.25;
/**
 * Full retina, capped there. The lights have a hard-edged core now, and an edge
 * drawn at one device pixel per CSS pixel is exactly the thing that reads as
 * pixellation once the sphere zooms in on it.
 */
const MAX_PIXEL_RATIO = 2;
const MAX_CANVAS_PIXELS = 4_000_000;

/**
 * The surface gets a far smaller budget than the lights do, on both counts.
 *
 * It is resampled twice over — painted into a buffer smaller than its canvas and
 * scaled up to it — where the lights are stamped at device resolution. That is
 * the right way round: the lights have hard cores that only survive at full
 * ratio, while the night surface is soft enough that the two rescales cost it
 * nothing anyone can see.
 */
const EARTH_MAX_PIXEL_RATIO = 2;
const EARTH_MAX_CANVAS_PIXELS = 2_600_000;
/**
 * Roughly how many pixels a pass of the surface may cost, moving and at rest.
 *
 * Unlike the lights, which are a fixed count wherever the sphere is, the surface
 * costs whatever the sphere covers — so a selection is its worst case by far. At
 * rest the disc is a seventh of the oversized canvas and the rows that miss it
 * are skipped outright; zoomed in it fills the thing edge to edge, and every one
 * of those pixels wants an `atan2` and an `asin`. Hence the gap between the two:
 * the moving pass has to hold a frame at full coverage, the settled one is paid
 * exactly once.
 */
const EARTH_TWEEN_PIXELS = 150_000;
const EARTH_STILL_PIXELS = 2_600_000;

const GRATICULE = geoGraticule10();
const DEGREES = Math.PI / 180;

type Coords = [number, number];

/** One country out of the Natural Earth collection, keyed by its ISO id. */
type CountryFeature = Feature<Geometry, { name: string }>;

/** The pieces of a marker the animation loops write to directly. */
type MarkerElements = {
  group: SVGGElement;
  rings: SVGCircleElement[];
  core: SVGCircleElement | null;
};

type UseGlobeProjectionOptions = {
  countries: FootprintCountry[];
  salesMarkets: Coords[];
  selectedKey: string | null;
};

function easeInOutCubic(k: number) {
  return k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
}

/** Shortest way round — keeps a rotation from taking the long way about. */
function shortestDelta(from: number, to: number) {
  return ((((to - from) % 360) + 540) % 360) - 180;
}

/**
 * Seeded PRNG. The satellite scatter has to land in the same place on every
 * load — `Math.random()` here would move the lights about between renders.
 */
function mulberry32(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;

    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);

    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Turn `[lon, lat, tier]` triples into a flat buffer the draw loop can walk
 * without allocating: each light keeps the first three components of its unit
 * vector *before* the view rotation (`a`, `b`, `c`), plus its size and opacity.
 * Rotating a precomputed vector is a handful of multiplies — calling
 * `projection([lon, lat])` for eleven thousand points would allocate a tuple per
 * point per frame and hand the garbage collector a full-time job.
 *
 * The buffer comes back sorted by size, largest first, so narrow screens can
 * simply stop the loop early rather than testing every point.
 */
function packLights(rows: [number, number, number][]) {
  const random = mulberry32(0x5e17aa7);
  const points: [number, number, number, number][] = [];

  for (const [lon, lat, tier] of rows) {
    points.push([lon, lat, TIER_SIZE[tier], TIER_ALPHA[tier]]);

    for (let index = 0; index < TIER_SATELLITES[tier]; index++) {
      points.push([
        lon + (random() - 0.5) * 2 * SATELLITE_SPREAD,
        lat + (random() - 0.5) * 2 * SATELLITE_SPREAD,
        TIER_SIZE[tier] * SATELLITE_SIZE,
        TIER_ALPHA[tier] * SATELLITE_ALPHA,
      ]);
    }
  }

  points.sort((left, right) => right[2] - left[2]);

  const packed = new Float64Array(points.length * LIGHT_STRIDE);
  const thinSize = TIER_SIZE[THIN_MIN_TIER] * SATELLITE_SIZE;
  let thinCount = 0;

  points.forEach(([lon, lat, size, alpha], index) => {
    const lambda = lon * DEGREES;
    const phi = lat * DEGREES;
    const cosPhi = Math.cos(phi);
    const offset = index * LIGHT_STRIDE;

    packed[offset] = Math.cos(lambda) * cosPhi;
    packed[offset + 1] = Math.sin(lambda) * cosPhi;
    packed[offset + 2] = Math.sin(phi);
    packed[offset + 3] = size;
    packed[offset + 4] = alpha;

    if (size >= thinSize) thinCount = index + 1;
  });

  return { packed, count: points.length, thinCount };
}

/**
 * One amber light, painted once and stamped for every city.
 *
 * Two parts, not one: a flat opaque core out to `LIGHT_CORE_STOP`, then a
 * shoulder that drops away steeply into a long, very faint halo. A single smooth
 * ramp from centre to edge — the obvious way to build it — has no core at all,
 * so every city is brightest at exactly one pixel and fades from there; stamp
 * eleven thousand of those over each other and the result is fog. The plateau is
 * what makes a city read as *lit*, and the long tail is what makes it glow.
 */
function createLightSprite() {
  const sprite = document.createElement("canvas");

  sprite.width = LIGHT_SPRITE_SIZE;
  sprite.height = LIGHT_SPRITE_SIZE;

  const context = sprite.getContext("2d");

  if (!context) return null;

  const middle = LIGHT_SPRITE_SIZE / 2;
  const gradient = context.createRadialGradient(
    middle,
    middle,
    0,
    middle,
    middle,
    middle,
  );

  // Sodium orange, which is what a city actually looks like from orbit and what
  // the reference is full of. Only the very centre keeps any white in it — that
  // is the lamp itself — and the colour runs hotter and more saturated the
  // further out it goes, so a cluster builds into an orange bloom rather than a
  // pale one. It stops short of the brand `#FE4F02`, which stays reserved for
  // the headquarters and the selected country: those have to keep reading as the
  // one thing on the sphere the visitor is meant to act on.
  gradient.addColorStop(0, "rgba(255,236,201,1)");
  gradient.addColorStop(LIGHT_CORE_STOP, "rgba(255,190,106,1)");
  gradient.addColorStop(LIGHT_CORE_STOP + 0.04, "rgba(255,158,54,0.62)");
  gradient.addColorStop(0.34, "rgba(252,132,24,0.24)");
  gradient.addColorStop(0.62, "rgba(240,116,12,0.08)");
  gradient.addColorStop(1, "rgba(240,116,12,0)");

  context.fillStyle = gradient;
  context.fillRect(0, 0, LIGHT_SPRITE_SIZE, LIGHT_SPRITE_SIZE);

  return sprite;
}

/**
 * Drives the Global Footprint globe: an orthographic projection whose rotation
 * tweens to centre the selected country, with land, graticule, markers, sales
 * dots and the city-light canvas reprojected on every frame.
 *
 * Everything the animation touches is written straight to the DOM through refs
 * — React state is reserved for the one-off "land has arrived" flip, so a
 * rotation never triggers a render.
 */
export function useGlobeProjection({
  countries,
  salesMarkets,
  selectedKey,
}: UseGlobeProjectionOptions) {
  const graticuleRef = useRef<SVGPathElement>(null);
  const landRef = useRef<SVGPathElement>(null);
  // The selected country is drawn as an outline, and the outline is several
  // strokes of the same shape stacked up — so the geometry lives on one path and
  // the fade lives on the group holding the copies of it.
  const highlightRef = useRef<SVGPathElement>(null);
  const highlightGroupRef = useRef<SVGGElement>(null);
  const trailRef = useRef<SVGPathElement>(null);
  const salesGroupRef = useRef<SVGGElement>(null);

  // The circles that make up the disc itself. They sit at different depths in
  // the layer stack — bloom, ocean, land, terminator, limb — so they cannot
  // share a wrapper group and each needs its own ref to follow the projection's
  // scale. The bloom sits at a multiple of it rather than on the sphere.
  const bloomRef = useRef<SVGCircleElement>(null);
  const oceanRef = useRef<SVGCircleElement>(null);
  const terminatorRef = useRef<SVGCircleElement>(null);
  const limbGlowRef = useRef<SVGCircleElement>(null);
  const limbRef = useRef<SVGCircleElement>(null);

  const lightsCanvasRef = useRef<HTMLCanvasElement>(null);
  // The surface photograph, on its own canvas beneath the SVG. It cannot share
  // the lights' canvas: that one blends `screen` so its black counts as
  // nothing, which is exactly what a night surface must not do.
  const earthCanvasRef = useRef<HTMLCanvasElement>(null);
  const earthTextureRef = useRef<EarthTexture | null>(null);
  const earthPainterRef = useRef<ReturnType<typeof createEarthPainter>>();

  const markersRef = useRef(new Map<string, MarkerElements>());
  const landDataRef = useRef<GeoPermissibleObjects | null>(null);
  // The same features the land path is drawn from, kept split by ISO id so the
  // selected country can be picked back out and painted over the top.
  const featuresByIdRef = useRef(new Map<string, CountryFeature>());
  // The id is held rather than the feature itself, so a country selected before
  // the atlas lands still lights up the moment it arrives.
  const highlightIdRef = useRef<string | null>(null);
  const isHighlightShownRef = useRef(false);
  const highlightTimerRef = useRef<number>();
  const rotationFrameRef = useRef(0);
  const pulseFrameRef = useRef(0);
  const selectedKeyRef = useRef(selectedKey);

  const lightsRef = useRef<ReturnType<typeof packLights> | null>(null);
  const lightSpriteRef = useRef<HTMLCanvasElement | null>(null);
  // Set for the duration of a rotation or zoom, so the light field knows to
  // paint its cheaper pass while the sphere is in motion.
  const isTweeningRef = useRef(false);

  const [isLandReady, setIsLandReady] = useState(false);
  // The flat ocean disc and the land fill are what the sphere is made of until
  // the photograph lands, and what it falls back to if the photograph never
  // does — so they are faded out rather than dropped, and only once it is up.
  const [isEarthReady, setIsEarthReady] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const projectionRef = useRef<GeoProjection>();

  if (!projectionRef.current) {
    const [lon, lat] = GLOBE_INITIAL_VIEW;

    projectionRef.current = geoOrthographic()
      .scale(GLOBE_RADIUS)
      .translate(GLOBE_CENTER)
      .rotate([-lon, -lat])
      .clipAngle(90);
  }

  const pathRef = useRef<ReturnType<typeof geoPath>>();

  if (!pathRef.current) {
    pathRef.current = geoPath(projectionRef.current);
  }

  const coordsOf = useCallback(
    (key: string | null) =>
      countries.find((country) => country.key === key)?.coords ?? null,
    [countries],
  );

  /**
   * Stamp every visible city onto the canvas overlay.
   *
   * The rotation is applied by hand rather than through `projection(coords)`:
   * this is the same maths d3 runs — a spin about the polar axis, then the
   * orthographic tilt — but reading straight out of the packed buffer, so a
   * frame costs no allocations at all. `x` doubles as the visibility test,
   * standing in for the projection's `clipAngle(90)`, which only clips paths.
   */
  const drawLights = useCallback(() => {
    const canvas = lightsCanvasRef.current;
    const lights = lightsRef.current;

    if (!canvas || !lights) return;

    const context = canvas.getContext("2d");

    if (!context) return;

    if (!lightSpriteRef.current) lightSpriteRef.current = createLightSprite();

    const sprite = lightSpriteRef.current;

    if (!sprite) return;

    // Nothing laid out yet, or the backing store has not been sized — either way
    // the transform below would be meaningless.
    if (!canvas.clientWidth || !canvas.width) return;

    // The element is wider than the stage it draws for, so unpick the overscan
    // to recover the box the viewBox units actually map onto.
    const boxWidth = canvas.clientWidth / (1 + OVERSCAN_X * 2);

    const projection = projectionRef.current!;
    const scale = projection.scale();
    const [translateX, translateY] = projection.translate();
    const [rotateLambda, rotatePhi, rotateGamma] = projection.rotate();

    const deltaLambda = rotateLambda * DEGREES;
    const cosLambda = Math.cos(deltaLambda);
    const sinLambda = Math.sin(deltaLambda);
    const cosPhi = Math.cos(rotatePhi * DEGREES);
    const sinPhi = Math.sin(rotatePhi * DEGREES);
    const cosGamma = Math.cos(rotateGamma * DEGREES);
    const sinGamma = Math.sin(rotateGamma * DEGREES);

    // Draw in viewBox units — the transform carries the device pixel ratio, the
    // CSS size and the overscan offset, so nothing downstream has to convert
    // coordinates and the sphere's centre stays put however far it is oversized.
    const ratio = canvas.width / canvas.clientWidth;
    const unit = (boxWidth / GLOBE_WIDTH) * ratio;
    const originX = OVERSCAN_X * GLOBE_WIDTH;
    const originY = OVERSCAN_Y * GLOBE_HEIGHT;

    context.setTransform(unit, 0, 0, unit, originX * unit, originY * unit);
    context.clearRect(
      -originX,
      -originY,
      GLOBE_WIDTH + originX * 2,
      GLOBE_HEIGHT + originY * 2,
    );
    // Overlapping halos add rather than cover, which is what turns a cluster of
    // neighbouring cities into one bright conurbation.
    context.globalCompositeOperation = "lighter";
    // The sprite is drawn well below its authored size at rest and above it at
    // zoom, so it is being resampled either way — and the cheap filter is what
    // turns the core's edge into stair-steps.
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    const sizeScale = (scale / GLOBE_RADIUS) ** ZOOM_SIZE_EXPONENT;
    const lightX = LIGHT_OFFSET[0];
    const lightY = LIGHT_OFFSET[1];

    // A full pass is some eleven thousand sprites and measures around 28ms on
    // this stage — fine once, far too slow to hold sixty frames a second. So
    // the small cities are dropped while the sphere is actually moving, and
    // painted back in on the frame it settles; nobody can pick out a two-pixel
    // light mid-rotation anyway. Narrow stages stay thinned throughout.
    // The buffer is sorted by size, so thinning is a shorter loop rather than a
    // test on every point.
    const total =
      isTweeningRef.current || boxWidth < THIN_BREAKPOINT
        ? lights.thinCount
        : lights.count;

    for (let index = 0; index < total; index++) {
      const offset = index * LIGHT_STRIDE;
      const a = lights.packed[offset];
      const b = lights.packed[offset + 1];
      const c = lights.packed[offset + 2];

      // Spin about the polar axis, then tilt — d3's `rotationPhiGamma`.
      const spunX = a * cosLambda - b * sinLambda;
      const spunY = b * cosLambda + a * sinLambda;
      const k = c * cosPhi + spunX * sinPhi;
      const x = spunX * cosPhi - c * sinPhi;

      if (x <= 0) continue; // on the far side of the sphere

      const y = spunY * cosGamma - k * sinGamma;
      const z = k * cosGamma + spunY * sinGamma;

      const screenX = translateX + scale * y;
      const screenY = translateY - scale * z;

      // Fade with distance from where the light falls, so the city lights sink
      // into the terminator instead of sitting flat on top of it.
      const shade =
        1 - LIGHT_FALLOFF * Math.min(1, Math.hypot(y - lightX, -z - lightY));

      context.globalAlpha = lights.packed[offset + 4] * shade;

      const size = lights.packed[offset + 3] * sizeScale;

      context.drawImage(
        sprite,
        screenX - size / 2,
        screenY - size / 2,
        size,
        size,
      );
    }

    context.globalAlpha = 1;
    context.globalCompositeOperation = "source-over";
  }, []);

  /**
   * Wrap the surface photograph around the sphere.
   *
   * All of the work is in `paintEarth`; what is decided here is how much of it
   * to do. The light vector handed over is the one every other layer keys off,
   * so the surface, the city lights and the SVG terminator all fall away from
   * the same point rather than disagreeing about where the sun is.
   */
  const drawEarth = useCallback(() => {
    const canvas = earthCanvasRef.current;
    const texture = earthTextureRef.current;

    if (!canvas || !texture) return;

    if (!earthPainterRef.current) {
      earthPainterRef.current = createEarthPainter();
    }

    earthPainterRef.current.paint({
      canvas,
      projection: projectionRef.current!,
      texture,
      stage: {
        width: GLOBE_WIDTH,
        height: GLOBE_HEIGHT,
        overscanX: OVERSCAN_X,
        overscanY: OVERSCAN_Y,
      },
      light: {
        offsetX: LIGHT_OFFSET[0],
        offsetY: LIGHT_OFFSET[1],
        falloff: EARTH_LIGHT_FALLOFF,
      },
      budget: isTweeningRef.current ? EARTH_TWEEN_PIXELS : EARTH_STILL_PIXELS,
    });
  }, []);

  /** As `resizeLightsCanvas`, on the surface's tighter budget. */
  const resizeEarthCanvas = useCallback(() => {
    const canvas = earthCanvasRef.current;

    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    if (!width || !height) return;

    const budgetRatio = Math.sqrt(EARTH_MAX_CANVAS_PIXELS / (width * height));
    const ratio = Math.min(
      EARTH_MAX_PIXEL_RATIO,
      budgetRatio,
      window.devicePixelRatio || 1,
    );
    const nextWidth = Math.round(width * ratio);
    const nextHeight = Math.round(height * ratio);

    if (canvas.width === nextWidth && canvas.height === nextHeight) return;

    canvas.width = nextWidth;
    canvas.height = nextHeight;
    drawEarth();
  }, [drawEarth]);

  /** Match the canvas backing store to its box, then repaint. */
  const resizeLightsCanvas = useCallback(() => {
    const canvas = lightsCanvasRef.current;

    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    if (!width || !height) return;

    // The overscan has already tripled the element's area, so cap the backing
    // store by total pixels as well as by ratio — a retina 4K stage would
    // otherwise allocate tens of megabytes to hold a field of soft blobs.
    const budgetRatio = Math.sqrt(MAX_CANVAS_PIXELS / (width * height));
    const ratio = Math.min(
      MAX_PIXEL_RATIO,
      budgetRatio,
      window.devicePixelRatio || 1,
    );
    const nextWidth = Math.round(width * ratio);
    const nextHeight = Math.round(height * ratio);

    if (canvas.width === nextWidth && canvas.height === nextHeight) return;

    canvas.width = nextWidth;
    canvas.height = nextHeight;
    drawLights();
  }, [drawLights]);

  /** Curve from the selected marker towards the detail panel. */
  const drawTrail = useCallback(() => {
    const trail = trailRef.current;

    if (!trail) return;

    const projection = projectionRef.current!;
    const coords = coordsOf(selectedKeyRef.current);
    const rotation = projection.rotate();
    const viewCenter: Coords = [-rotation[0], -rotation[1]];

    // No selection, or the country has spun round the back — nothing to point at.
    if (!coords || geoDistance(coords, viewCenter) > VISIBLE_ANGLE) {
      trail.style.opacity = "0";
      return;
    }

    const point = projection(coords);

    if (!point) {
      trail.style.opacity = "0";
      return;
    }

    const [x, y] = point;
    // The panel sits on the trailing edge, which flips with the document.
    const isRTL = document.documentElement.dir === "rtl";
    const targetX = isRTL ? 30 : GLOBE_WIDTH - 30;
    const targetY = GLOBE_HEIGHT * 0.3;
    const midX = (x + targetX) / 2;

    trail.setAttribute(
      "d",
      `M${x},${y} C${midX},${y} ${midX},${targetY} ${targetX},${targetY}`,
    );
    // A gradient runs left-to-right whatever the path does, so mirror it to
    // keep the solid end anchored on the marker.
    trail.setAttribute("stroke", `url(#fp-trail${isRTL ? "-rtl" : ""})`);
    trail.style.opacity = "0.55";
  }, [coordsOf]);

  /** Reproject every layer against the projection's current rotation and scale. */
  const redraw = useCallback(() => {
    const projection = projectionRef.current!;
    const path = pathRef.current!;
    const scale = projection.scale();

    // The disc is drawn as plain circles rather than projected geometry, so it
    // has to be resized by hand to keep pace with the land as we zoom. Each
    // layer carries the multiple of the sphere's radius it sits at — leave one
    // out and it detaches from the globe the moment a country is selected.
    const discs: [RefObject<SVGCircleElement>, number][] = [
      [bloomRef, ATMOSPHERE_SPREAD],
      [oceanRef, 1],
      [terminatorRef, 1],
      [limbGlowRef, 1],
      [limbRef, 1],
    ];

    for (const [disc, factor] of discs) {
      disc.current?.setAttribute("r", String(scale * factor));
    }

    // Zoomed in you are close enough that the halo would swamp the section, and
    // close enough that you would not see much of it anyway.
    if (bloomRef.current) {
      bloomRef.current.style.opacity = String(
        Math.max(0, Math.min(1, 1 - (scale / GLOBE_RADIUS - 1) / 1.2)),
      );
    }

    graticuleRef.current?.setAttribute("d", path(GRATICULE) ?? "");

    if (landRef.current && landDataRef.current) {
      landRef.current.setAttribute("d", path(landDataRef.current) ?? "");
    }

    // The highlight tracks a ref rather than the prop: `redraw` is a dependency
    // of the world-data fetch, so rebuilding it on every selection would refetch
    // the atlas each time a country is clicked.
    if (highlightRef.current) {
      const highlighted = highlightIdRef.current
        ? featuresByIdRef.current.get(highlightIdRef.current)
        : null;

      highlightRef.current.setAttribute(
        "d",
        highlighted ? (path(highlighted) ?? "") : "",
      );
    }

    const rotation = projection.rotate();
    const viewCenter: Coords = [-rotation[0], -rotation[1]];

    // Land and graticule are clipped by `clipAngle`, but individual points are
    // not — they project onto the disc from either side, so cull them by hand.
    const place = (element: SVGElement, coords: Coords) => {
      const point =
        geoDistance(coords, viewCenter) > VISIBLE_ANGLE
          ? null
          : projection(coords);

      if (!point) {
        element.style.display = "none";
        return;
      }

      element.style.display = "";
      element.setAttribute("transform", `translate(${point[0]},${point[1]})`);
    };

    markersRef.current.forEach(({ group }, key) => {
      const coords = coordsOf(key);

      if (coords) place(group, coords);
    });

    const salesDots = salesGroupRef.current?.children;

    if (salesDots) {
      for (let index = 0; index < salesDots.length; index++) {
        const coords = salesMarkets[index];

        if (coords) place(salesDots[index] as SVGElement, coords);
      }
    }

    drawTrail();
    // Surface first, then the lights over the top of it — the lights blend
    // `screen` against whatever is behind them, and what is behind them is now
    // a photograph rather than a flat fill.
    drawEarth();
    drawLights();
  }, [coordsOf, drawEarth, drawLights, drawTrail, salesMarkets]);

  /**
   * Tween the sphere towards a view: an optional rotation that brings a country
   * round to face the viewer, and a scale that zooms in on it. Both run in the
   * one loop so they can never fight over the frame handle, and both read their
   * starting point off the projection — so retargeting mid-tween picks up from
   * wherever the sphere currently is rather than snapping.
   */
  const animateView = useCallback(
    ({
      rotation,
      scale,
    }: {
      rotation?: [number, number, number];
      scale: number;
    }) => {
      const projection = projectionRef.current!;
      const fromRotation = projection.rotate();
      const fromScale = projection.scale();
      const scaleDelta = scale - fromScale;

      cancelAnimationFrame(rotationFrameRef.current);
      // Clear it up front: retargeting mid-tween cancels the loop above, and
      // every branch below either finishes at rest or sets it again itself.
      isTweeningRef.current = false;

      // Nothing to move — the opening render lands here, so redraw rather than
      // spinning a loop that would tween everything to where it already is.
      if (!rotation && Math.abs(scaleDelta) < 0.5) {
        redraw();
        return;
      }

      if (prefersReducedMotion) {
        if (rotation) projection.rotate(rotation);

        projection.scale(scale);
        redraw();
        return;
      }

      const rotationDeltas = rotation?.map((value, index) =>
        shortestDelta(fromRotation[index], value),
      );
      const start = performance.now();

      const step = (now: number) => {
        const progress = Math.min(1, (now - start) / ROTATION_DURATION);
        const eased = easeInOutCubic(progress);

        if (rotationDeltas) {
          projection.rotate([
            fromRotation[0] + rotationDeltas[0] * eased,
            fromRotation[1] + rotationDeltas[1] * eased,
            fromRotation[2] + rotationDeltas[2] * eased,
          ]);
        }

        projection.scale(fromScale + scaleDelta * eased);

        if (progress < 1) {
          redraw();
          rotationFrameRef.current = requestAnimationFrame(step);
          return;
        }

        // Settled: repaint once at full detail, so the small lights the moving
        // pass skips are all back before the globe comes to rest.
        isTweeningRef.current = false;
        redraw();
      };

      isTweeningRef.current = true;
      rotationFrameRef.current = requestAnimationFrame(step);
    },
    [prefersReducedMotion, redraw],
  );

  /** Callback ref handed to each marker so the loops can reach its elements. */
  const registerMarker = useCallback(
    (key: string, group: SVGGElement | null) => {
      if (!group) {
        markersRef.current.delete(key);
        return;
      }

      markersRef.current.set(key, {
        group,
        rings: Array.from(
          group.querySelectorAll<SVGCircleElement>("[data-pulse-ring]"),
        ),
        core: group.querySelector<SVGCircleElement>("[data-marker-core]"),
      });
    },
    [],
  );

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");

    setPrefersReducedMotion(query.matches);

    const onChange = (event: MediaQueryListEvent) =>
      setPrefersReducedMotion(event.matches);

    query.addEventListener("change", onChange);

    return () => query.removeEventListener("change", onChange);
  }, []);

  // Land arrives after first paint; the sphere, graticule and markers are up
  // straight away so the section is never blank.
  useEffect(() => {
    let cancelled = false;

    fetch(WORLD_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`world data: ${response.status}`);

        return response.json() as Promise<Topology>;
      })
      .then((topology) => {
        if (cancelled) return;

        const collection = feature(
          topology,
          topology.objects.countries as GeometryCollection,
        ) as unknown as FeatureCollection<Geometry, { name: string }>;

        landDataRef.current = collection as unknown as GeoPermissibleObjects;
        featuresByIdRef.current = new Map(
          collection.features.map((country) => [String(country.id), country]),
        );

        setIsLandReady(true);
        redraw();
      })
      .catch(() => {
        // The globe reads fine without land — sphere, graticule and markers
        // still carry the section, so a failed fetch is not worth surfacing.
      });

    return () => {
      cancelled = true;
    };
  }, [redraw]);

  // The surface photograph, on the same terms as everything else: fetched after
  // first paint, and silently skipped if it never arrives. Until then — and for
  // good if the fetch fails — the sphere falls back to the flat ocean gradient
  // and the country outlines over it, which is what it looked like before.
  useEffect(() => {
    let cancelled = false;

    loadEarthTexture()
      .then((texture) => {
        if (cancelled) return;

        earthTextureRef.current = texture;
        resizeEarthCanvas();
        drawEarth();
        setIsEarthReady(true);
      })
      .catch(() => {
        // A globe drawn from outlines alone is still a globe.
      });

    return () => {
      cancelled = true;
    };
  }, [drawEarth, resizeEarthCanvas]);

  // City lights, on the same terms as the land: fetched after first paint, and
  // silently skipped if they never arrive.
  useEffect(() => {
    let cancelled = false;

    fetch(LIGHTS_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`city lights: ${response.status}`);

        return response.json() as Promise<[number, number, number][]>;
      })
      .then((rows) => {
        if (cancelled) return;

        lightsRef.current = packLights(rows);
        resizeLightsCanvas();
        drawLights();
      })
      .catch(() => {
        // An unlit globe is still a globe.
      });

    return () => {
      cancelled = true;
    };
  }, [drawLights, resizeLightsCanvas]);

  // Both canvases are sized in CSS and drawn in device pixels, so their backing
  // stores have to be rebuilt whenever the stage changes width. One observer
  // over the pair: they are stretched across the same box and always change
  // together.
  useEffect(() => {
    const lights = lightsCanvasRef.current;
    const earth = earthCanvasRef.current;

    if (!lights && !earth) return;

    const resize = () => {
      resizeLightsCanvas();
      resizeEarthCanvas();
    };

    resize();

    const observer = new ResizeObserver(resize);

    if (lights) observer.observe(lights);
    if (earth) observer.observe(earth);

    return () => observer.disconnect();
  }, [resizeEarthCanvas, resizeLightsCanvas]);

  // First projection pass, once the refs are attached.
  useEffect(() => {
    redraw();
  }, [redraw]);

  // Cross-fade the highlight so the colour never jumps between landmasses: the
  // outgoing country fades as the sphere turns away from it, the shape is
  // swapped over while nothing is showing, and the incoming one fades up as the
  // rotation lands. The two halves add up to `ROTATION_DURATION`, so the colour
  // arrives exactly as the globe settles.
  useEffect(() => {
    const highlight = highlightGroupRef.current;

    if (!highlight) return;

    const nextId =
      countries.find(({ key }) => key === selectedKey)?.topoId ?? null;

    window.clearTimeout(highlightTimerRef.current);

    // Deselecting holds the last shape on the path and just fades it out —
    // clearing the id here would blank the path on the very next frame and the
    // highlight would snap off instead.
    if (!nextId) {
      highlight.style.opacity = "0";
      isHighlightShownRef.current = false;
      return;
    }

    const show = () => {
      highlightIdRef.current = nextId;
      isHighlightShownRef.current = true;
      highlight.style.opacity = "1";
      redraw();
    };

    // Nothing is on screen to fade out of — the first selection, or one made
    // after a deselect — so bring the new country straight up.
    if (
      !isHighlightShownRef.current ||
      highlightIdRef.current === nextId ||
      prefersReducedMotion
    ) {
      show();
      return;
    }

    highlight.style.opacity = "0";
    isHighlightShownRef.current = false;
    highlightTimerRef.current = window.setTimeout(show, HIGHLIGHT_FADE);
  }, [countries, prefersReducedMotion, redraw, selectedKey]);

  useEffect(() => {
    selectedKeyRef.current = selectedKey;

    const coords = coordsOf(selectedKey);

    // Deselecting leaves the globe facing where it is and just pulls back out.
    // The trail goes with it: `redraw` runs `drawTrail` on every frame of the
    // tween, and the ref above has already been cleared.
    if (!coords) {
      animateView({ scale: GLOBE_RADIUS });
      return;
    }

    animateView({
      rotation: [-coords[0], -coords[1] - SELECTION_TILT, 0],
      scale: GLOBE_RADIUS * SELECTION_ZOOM,
    });
  }, [animateView, coordsOf, selectedKey]);

  // Expanding rings under every marker, and a breathing core under the selected
  // one. Unlike `redraw`, this runs for the life of the section — but it only
  // ever touches a handful of circles, so it costs nothing at rest.
  useEffect(() => {
    if (prefersReducedMotion) return;

    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      let index = 0;

      markersRef.current.forEach(({ rings, core }, key) => {
        const offset = index++ * PULSE_STAGGER;
        const isSelected = selectedKeyRef.current === key;
        const isMuted = selectedKeyRef.current !== null && !isSelected;

        rings.forEach((ring, ringIndex) => {
          if (isMuted) {
            ring.style.opacity = "0";
            return;
          }

          const phase = (elapsed / PULSE_PERIOD + offset + ringIndex * 0.5) % 1;

          ring.setAttribute(
            "r",
            String(PULSE_MIN_RADIUS + phase * PULSE_GROWTH),
          );
          ring.style.opacity = String(
            (RING_OPACITY[ringIndex] ?? 0.28) * (1 - phase),
          );
        });

        if (core) {
          core.setAttribute(
            "r",
            String(
              isSelected
                ? CORE_RADIUS +
                    CORE_BREATH * Math.sin(elapsed / CORE_PERIOD + offset)
                : CORE_RADIUS,
            ),
          );
        }
      });

      pulseFrameRef.current = requestAnimationFrame(tick);
    };

    pulseFrameRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(pulseFrameRef.current);
  }, [prefersReducedMotion]);

  useEffect(
    () => () => {
      cancelAnimationFrame(rotationFrameRef.current);
      cancelAnimationFrame(pulseFrameRef.current);
      window.clearTimeout(highlightTimerRef.current);
    },
    [],
  );

  return {
    graticuleRef,
    landRef,
    highlightRef,
    highlightGroupRef,
    trailRef,
    salesGroupRef,
    bloomRef,
    oceanRef,
    terminatorRef,
    limbGlowRef,
    limbRef,
    lightsCanvasRef,
    earthCanvasRef,
    registerMarker,
    isLandReady,
    isEarthReady,
    prefersReducedMotion,
  };
}
